import { useCallback, useMemo } from 'react';

import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import {
  GeoJsonOverlay,
  MarkerLayer,
  type OverlayLayer,
  SearchPin,
  urlValueToLatLng,
} from '@atom/map';

import ArcgisLayerIcon from './arcgis/ArcgisLayerIcon';
import { useArcgisLayers } from './arcgis/ArcgisLayersProvider';
import { useKmlLayers } from './kml/KmlLayersProvider';
import { useMapPortalData } from './MapPortalDataProvider';

const MapPortalLayers = () => {
  const { data } = useMapPortalData();
  const { layers: arcgisLayers } = useArcgisLayers();
  const { layers: kmlOverlayLayers } = useKmlLayers();

  const [{ id, stack, hover, disabled, searchPoint }, { updateSearchParams }] =
    useNavigation({
      id: SearchParamType.STRING,
      stack: SearchParamType.STRING,
      hover: SearchParamType.STRING,
      disabled: SearchParamType.ARRAY,
      searchPoint: SearchParamType.STRING,
    });

  const activeId: string = id ?? '';
  const activeStack: string = stack ?? '';
  const hoverId: string = hover ?? '';
  const disabledIds: ReadonlySet<string> = useMemo(
    () => new Set(disabled ?? []),
    [disabled],
  );

  const onAssetGroupClick = useCallback(
    (stackKey: string) => {
      updateSearchParams({
        stack: stackKey,
        stackType: 'asset',
        view: 'list',
        hover: null,
        id: null,
      });
    },
    [updateSearchParams],
  );

  const onWorkOrderGroupClick = useCallback(
    (stackKey: string) => {
      updateSearchParams({
        stack: stackKey,
        stackType: 'workOrder',
        view: 'list',
        hover: null,
        id: null,
      });
    },
    [updateSearchParams],
  );

  const onMarkerClick = useCallback(
    (markerId: string, markerView: string) => {
      updateSearchParams({
        id: markerId,
        view: markerView,
        hover: null,
        stack: null,
        stackType: null,
      });
    },
    [updateSearchParams],
  );

  const searchPinPosition = searchPoint ? urlValueToLatLng(searchPoint) : null;

  const arcgisOverlayLayers: OverlayLayer[] = useMemo(
    () =>
      arcgisLayers
        .filter(layer => layer.visible)
        .map(layer => ({
          collection: layer.features,
          symbology: layer.symbology,
          fieldAliases: layer.fieldAliases,
          onFeatureClick: null,
          popupHeader: {
            icon: (
              <ArcgisLayerIcon
                iconType={layer.iconType}
                colorId={layer.colorId}
                size="1.5rem"
              />
            ),
            name: layer.name,
          },
        })),
    [arcgisLayers],
  );

  const overlayLayers = useMemo(
    () => [...arcgisOverlayLayers, ...kmlOverlayLayers],
    [arcgisOverlayLayers, kmlOverlayLayers],
  );
  const showGeoJsonOverlay = overlayLayers.length > 0;

  return (
    <>
      <MarkerLayer
        data={data}
        activeId={activeId}
        activeStack={activeStack}
        hoverId={hoverId}
        disabledIds={disabledIds}
        onAssetGroupClick={onAssetGroupClick}
        onWorkOrderGroupClick={onWorkOrderGroupClick}
        onMarkerClick={onMarkerClick}
      />
      <SearchPin position={searchPinPosition} />
      {showGeoJsonOverlay && <GeoJsonOverlay layers={overlayLayers} />}
    </>
  );
};

export default MapPortalLayers;
